import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DeleteAssessmentActivitiesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useTRPC } from '@/utils/trpc';

const mapTrpcResponseToGraphQL = (
  deletedCount: number
): DeleteAssessmentActivitiesMutation => {
  return {
    delete_assessment_activity: {
      __typename: 'assessment_activity_mutation_response',
      affected_rows: deletedCount,
    },
  };
};

export const useDeleteAssessmentActivitiesTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.assessment.deleteActivities.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.activityRegister.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.activityById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.assessmentActivitiesByParentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.assessmentRCSAActivitiesByAssessmentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.assessment.oversightActivityRegister.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [trpcEnabled, mutation.error, addNotification]);

  return {
    deleteAssessmentActivities: async (variables: {
      Ids: string[];
    }): Promise<DeleteAssessmentActivitiesMutation> => {
      await mutation.mutateAsync(variables);

      // The data-layer throws NotFound when 0 rows are deleted, so a
      // successful response guarantees all requested IDs were deleted.
      return mapTrpcResponseToGraphQL(variables.Ids.length);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
